import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Metric, EmptyState, SectionLabel, priceOf, PricingPanel } from "@/components/Metric";
import { SaveQuoteBar } from "@/components/SaveQuote";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Calculator, Layers, Tag } from "lucide-react";

const tierLabel = (tiers, i) => {
  const lo = i === 0 ? 1 : Number(tiers[i - 1].max) + 1;
  return Number(tiers[i].max) >= 99999 ? `${lo}+` : `${lo}–${tiers[i].max}`;
};

export function GangSheets() {
  const [sheets, setSheets] = useState(1);
  const [designFee, setDesignFee] = useState(false);
  const [res, setRes] = useState(null);
  const calc = async () => {
    try { const { data } = await api.post("/calc/apparel/sheets", { sheets: +sheets, design_fee: designFee }); setRes(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl p-6 h-fit">
        <h3 className="font-head font-bold mb-1">DTF gang sheets</h3>
        <p className="text-xs text-slate-500 mb-4">Transfers only — the customer presses them.</p>
        <Label className="text-xs">Number of sheets</Label>
        <Input data-testid="gang-sheets-qty" type="number" min="1" value={sheets} onChange={(e) => setSheets(e.target.value)} className="rounded-lg mt-1 num" />
        <div className="flex items-center justify-between py-3"><Label className="text-xs">Design / artwork fee</Label><Switch data-testid="gang-design-fee" checked={designFee} onCheckedChange={setDesignFee} /></div>
        <Button data-testid="calc-gang-button" onClick={calc} className="w-full bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg h-11"><Calculator size={16} className="mr-2" />Generate Quote</Button>
      </div>
      <div className="lg:col-span-7">
        {!res ? <EmptyState>Enter the number of sheets and calculate.</EmptyState> : (
          <div className="space-y-4" data-testid="gang-results">
            <div className="grid grid-cols-3 gap-3">
              <Metric icon={Layers} label="Sheets" value={`${res.sheets} × ${res.sheet_w}×${res.sheet_h}"`} />
              <Metric icon={Tag} label="Per sheet" value={money(res.sheet_unit_retail ?? res.wholesale_unit)} />
              {priceOf(res) != null && <Metric icon={Tag} label="Total" value={money(priceOf(res))} accent />}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <SectionLabel>Price per sheet</SectionLabel>
              <div className="flex flex-wrap gap-2 mb-3">
                {res.tiers.map((t, i) => <span key={i} className="text-xs border border-slate-200 rounded-full px-3 py-1 num">{tierLabel(res.tiers, i)} sheets · {money(t.price)}</span>)}
              </div>
              {res.design_fee_retail > 0 && <div className="text-xs text-slate-600 mb-2" data-testid="gang-design-fee-line">Includes design / artwork fee: {money(res.design_fee_retail)}</div>}
              <PricingPanel r={res} />
              <div className="mt-3 flex justify-end"><SaveQuoteBar module="DTF Gang Sheets" title={`DTF gang sheets x${res.sheets}`} inputs={{ sheets, designFee }} summary={res} /></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
